<template>
  <div class="usage-item">
    <div class="usage-content">
      <span class="usage-name">{{ usage.referencer_name }}</span>

      <div class="usage-actions">
        <button
          class="action-btn ghost"
          :class="{ active: showPath }"
          @click="togglePath">
          <i
            class="material-icons small"
            style="font-size: 20px">
            {{ showPath ? "bug_report" : "bug_report" }}
          </i>
        </button>

        <a
          class="link-btn ghost"
          :href="fullLink"
          target="_blank"
          rel="noopener noreferrer">
          <i class="material-icons small"> open_in_new </i>
        </a>
      </div>
    </div>

    <div
      v-if="showPath"
      class="path-details">
      <div v-if="loadingPaths">
        <b-skeleton
          width="100%"
          height="20" />
      </div>
      <div
        v-else-if="paths.length === 0"
        class="path-empty">
        No paths found
      </div>
      <div v-else>
        <div
          v-for="(path, pIdx) in paths"
          :key="pIdx"
          class="path-chain">
          <span
            v-for="(node, nIdx) in path"
            :key="nIdx"
            class="path-node">
            <span class="node-name">{{ node.name }}</span>
            <span class="node-type">({{ node.type }})</span>
            <span
              v-if="nIdx < path.length - 1"
              class="path-arrow">&rarr;</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { isEmbedded } from '@/utils/parentNav'

  const PARENT_ORIGIN = process.env.VUE_APP_PARENT_ORIGIN

  export default {
    name: 'UsageItem',
    props: {
      usage: {
        type: Object,
        required: true,
      },
      sourceId: {
        type: [String, Number,],
        default: null,
      },
      sourceType: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        showPath: false,
        paths: [],
        loadingPaths: false,
        pathsLoaded: false,
      }
    },
    computed: {
      computedLink () {
        const type = this.usage.referencer_type
        const id = this.usage.referencer_id
        const companyId = this.$route.params.company_id
        const key = this.usage.key || this.usage.referencer_id

        switch (type) {
          case 'EXPRESSION':
            return `/company/${companyId}/tools/expressions?id=${id}`
          case 'DASHBOARD_MODULE':
            return `/company/${companyId}/dashboard/${id}`
          case 'PDF_REPORT':
            return `/company/${companyId}/tools/email_report_builder/${id}`
          case 'ATTRIBUTE':
            return `/company/${companyId}/data/entity_attributes?entity=${id}`
          case 'COMPANY_MEASURE':
            return `/company/${companyId}/definitions?tab=measures&key=${key}`
          case 'COMPANY_DIMENSION':
            return `/company/${companyId}/definitions?tab=dimensions&key=${key}`
          case 'COMPANY_FILTER':
            return `/company/${companyId}/definitions?tab=filters&key=${key}`
          case 'COMPANY_EXPRESSION':
            return `/company/${companyId}/tools/reports/config?tab=companyExpression&key=${key}`
          default:
            return '#'
        }
      },
      fullLink () {
        const path = this.computedLink
        if (isEmbedded && PARENT_ORIGIN) {
          return PARENT_ORIGIN + path
        }
        return path
      },
    },
    methods: {
      async togglePath () {
        this.showPath = !this.showPath
        if (this.showPath && !this.pathsLoaded && this.sourceId != null) {
          this.loadingPaths = true
          try {
            const result = await this.$apis.expressions.getUsagePaths(
              this.sourceId,
              this.sourceType,
              this.usage.referencer_id,
              this.usage.referencer_type
            )
            this.paths = result?.paths || []
          } catch (e) {
            this.paths = []
          } finally {
            this.loadingPaths = false
            this.pathsLoaded = true
          }
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.usage-item {
  padding: 8px 10px;
  margin: 8px 16px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.usage-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.usage-name {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}

.usage-actions {
  display: flex;
  gap: 8px;
}

.action-btn,
.link-btn {
  padding: 4px 8px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    color: #374151;
    background: #f3f4f6;
  }

  &.ghost {
    background: transparent;
  }

  &.active {
    color: #374151;
    background: #f3f4f6;
  }

  .material-icons.small {
    font-size: 16px;
  }
}

.path-details {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 12px;
  color: #6b7280;

  .path-label {
    font-weight: 600;
    margin-right: 6px;
    color: #4b5563;
  }

  .path-empty {
    color: #9ca3af;
    font-style: italic;
  }

  .path-chain {
    margin-bottom: 6px;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .path-node {
    .node-name {
      font-weight: 500;
      color: #374151;
    }

    .node-type {
      color: #9ca3af;
      font-size: 11px;
      margin-left: 2px;
    }
  }

  .path-arrow {
    margin: 0 4px;
    color: #9ca3af;
  }
}
</style>
